import React, { useState } from "react";
import { SplitContentDynamicTemplateConfig } from "../splitContentTypes";
import { Template } from "@/lib/types";
import { TemplateSelector } from "@/pages/template/shared/TemplateSelector";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TemplatePreview } from "@/pages/template/shared/TemplatePreview";
import { cn } from "@/lib/utils";
import { getCheckeredBg } from "@/lib/tailwindUtils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DynamicTextInfo,
  extractDynamicTextFromCanvasData,
  removeMustache,
} from "@/components/canvas/utils/canvasUtils";
import { DynamicTemplateDataSourceResponseItem } from "@/pages/content/contentTypes";

const formSchema = z.object({
  dataSourceUrl: z.string().min(1),
  pollingIntervalSeconds: z.string().min(1).optional(),
});

type FormSchemaType = z.infer<typeof formSchema>;

interface Props {
  onClose: () => void;
  onAttached: (config: SplitContentDynamicTemplateConfig) => void;
  currentConfig?: SplitContentDynamicTemplateConfig;
}

const constructExampleDataSourceResponse = (
  dynamicTexts: DynamicTextInfo[]
): DynamicTemplateDataSourceResponseItem[] => {
  const expectedDataSourceResponse: DynamicTemplateDataSourceResponseItem[] = [];
  dynamicTexts.map((textObj) => {
    textObj.dynamicValues.forEach((dynamicValue) => {
      expectedDataSourceResponse.push({
        key: removeMustache(dynamicValue),
        value: "123 etc..",
      });
    });
  });
  return expectedDataSourceResponse;
};

const DEFAULT_POLLING_INTERVAL_SECONDS = 30;

export const SplitContentAttachDynamicTemplateDialog: React.FC<Props> = ({ onClose, onAttached, currentConfig }) => {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState<Template>();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dataSourceUrl: "",
      pollingIntervalSeconds: DEFAULT_POLLING_INTERVAL_SECONDS.toString(),
    },
  });

  const onSubmit = (values: FormSchemaType) => {
    if (!selectedTemplate) {
      return;
    }
    const conf: SplitContentDynamicTemplateConfig = {
      type: "dynamic-template",
      templateId: selectedTemplate.id,
      templateCanvasData: JSON.stringify(selectedTemplate.canvas_data),
      dataSourceUrl: values.dataSourceUrl,
      pollingIntervalSeconds: Number(values.pollingIntervalSeconds) ?? DEFAULT_POLLING_INTERVAL_SECONDS,
    };
    onAttached(conf);
    onClose();
  };

  const dynamicTexts = selectedTemplate ? extractDynamicTextFromCanvasData(selectedTemplate.canvas_data) : [];
  const expectedDataSourceResponse = constructExampleDataSourceResponse(dynamicTexts);

  return (
    <>
      <TemplateSelector
        onSelectTemplate={setSelectedTemplate}
        onClose={onClose}
        selectedTemplateId={currentConfig?.templateId}
      />
      {selectedTemplate && (
        <Dialog
          open
          onOpenChange={() => {
            setSelectedTemplate(undefined);
          }}
        >
          <DialogContent className="max-w-[95vw] sm:max-w-[90vw] h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("Attach dynamic template")}</DialogTitle>
              <DialogDescription>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <FormField
                          control={form.control}
                          name="dataSourceUrl"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("splitContent.dataSourceUrl")}</FormLabel>
                              <FormControl>
                                <Input placeholder={t("splitContent.dataSourceUrl")} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="pollingIntervalSeconds"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("splitContent.pollingIntervalSeconds")}</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t("splitContent.pollingIntervalSeconds")}
                                  type="number"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {dynamicTexts.length ? (
                          <>
                            <div className="mt-2 border p-2 rounded-md bg-muted">
                              {dynamicTexts.map((textObj) => (
                                <div key={textObj.indexInCanvasData} className="flex flex-row gap-2 items-center">
                                  <span className="w-[120px]">{t("Binding text")}</span>
                                  <span className="text-sm break-words">
                                    {selectedTemplate.canvas_data.objects[textObj.indexInCanvasData].text}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <Alert className="mt-2" variant="info">
                              <AlertTitle>{t("Expected Data source response format based on template:")}</AlertTitle>
                              <AlertDescription>
                                <pre className="overflow-x-auto text-xs md:text-sm">
                                  {JSON.stringify(expectedDataSourceResponse, null, 2)}
                                </pre>
                              </AlertDescription>
                            </Alert>
                          </>
                        ) : null}
                      </div>
                      <div>
                        <div
                          className={cn(getCheckeredBg(), "p-2 md:p-4 max-h-[300px] md:max-h-[600px] overflow-y-auto")}
                        >
                          <TemplatePreview data={selectedTemplate.canvas_data} previewSize="full" />
                        </div>
                      </div>
                    </div>
                    <Button type="submit" className="w-full md:w-auto">
                      {t("splitContent.attachDynamicTemplate")}
                    </Button>
                  </form>
                </Form>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
